/**
 * SchemaSqlPreview — Bottom panel showing source DDL (read-only) and target DDL (editable).
 * Split left/right view. Updates when user selects an object in either tree.
 */

import { useState, useEffect } from 'react'

function SchemaSqlPreview({ selectedObject, convertedSchema, onEditTargetDdl }) {
  const [editedDdl, setEditedDdl] = useState('')

  // Sync the edited DDL when selection changes
  useEffect(() => {
    if (!selectedObject || !convertedSchema) {
      setEditedDdl('')
      return
    }

    const { type, name } = selectedObject

    // Find the converted object
    let targetDdl = ''
    if (type === 'table') {
      const table = convertedSchema.tables.find(t => t.name === name)
      targetDdl = table?.convertedDdl || table?.rawDdl || ''
    } else if (type === 'procedure') {
      const proc = convertedSchema.procedures.find(p => p.name === name)
      targetDdl = proc?.convertedDdl || proc?.rawDdl || ''
    } else if (type === 'view') {
      const view = convertedSchema.views.find(v => v.name === name)
      targetDdl = view?.rawDdl || ''
    } else if (type === 'index') {
      const index = convertedSchema.indexes.find(i => i.name === name)
      targetDdl = index?.rawDdl || ''
    }

    setEditedDdl(targetDdl)
  }, [selectedObject, convertedSchema])

  // Get source DDL for the selected object
  const getSourceDdl = () => {
    if (!selectedObject) return ''
    return selectedObject.data?.rawDdl || ''
  }

  const handleDdlChange = (e) => {
    const newDdl = e.target.value
    setEditedDdl(newDdl)
    if (onEditTargetDdl && selectedObject) {
      onEditTargetDdl(selectedObject.type, selectedObject.name, newDdl)
    }
  }

  if (!selectedObject) {
    return (
      <div className="schema-sql-preview">
        <div className="schema-sql-empty">
          Select an object from the tree to view its DDL
        </div>
      </div>
    )
  }

  return (
    <div className="schema-sql-preview">
      {/* Source DDL (left, read-only) */}
      <div className="schema-sql-pane">
        <div className="schema-sql-pane-header">
          Source DDL — {selectedObject.name}
        </div>
        <pre className="schema-sql-source">{getSourceDdl()}</pre>
      </div>

      {/* Target DDL (right, editable) */}
      <div className="schema-sql-pane">
        <div className="schema-sql-pane-header">
          Target DDL {convertedSchema ? '(editable)' : ''} — {selectedObject.name}
        </div>
        {convertedSchema ? (
          <textarea
            className="schema-sql-target"
            value={editedDdl}
            onChange={handleDdlChange}
            spellCheck={false}
          />
        ) : (
          <pre className="schema-sql-source schema-sql-pending">
            Click "Convert" to generate target DDL
          </pre>
        )}
      </div>
    </div>
  )
}

export default SchemaSqlPreview
